import {
  fetchSharedProject, approveRoomViaShare, submitRoomFeedbackViaShare,
  type SharedProject,
} from '@/lib/data/share';
import { esc, fmtDate, fmtMoney, setPageTitle, setTitleBase } from '@/lib/dom';
import { approvalDetail, completionStamp, clientRoomControls } from '@/lib/room-status';
import {
  ROOM_STATUS_LABEL, SPEC_STATUS_LABEL, SCHEDULE_STATUS_LABEL,
} from '@/lib/labels';
import { upcomingEntries } from '@/lib/schedule-grouping';
import { fetchShareBranding, logoUrl } from '@/lib/data/branding';
import {
  companyName, logoPath, photoPath, DEFAULT_COMPANY, type Branding,
} from '@/lib/branding';
import { brandLockup } from '@/lib/brand-lockup';
import { ensureBrandFont } from '@/lib/brand-font-loader';
import { QUESTIONNAIRE_FIELDS, readResponses } from '@/lib/data/questionnaires';
import { signPortalMedia } from '@/lib/data/portal-media';
import { hydratePortalImages } from '@/lib/portal-images';
import {
  acknowledgeChangeOrderViaShare, askChangeOrderQuestion, CHANGE_ORDER_STATUS_LABEL,
} from '@/lib/data/change-orders';
import {
  parseShareRoute, shareHref, imageSource, mediaImg, groupRoomImages,
  roomsByLocation, roomPage,
} from '@/lib/share-room';
import { summarizeChangeOrders, revisedBudgetCents, changeOrderTotal } from '@/lib/change-order-budget';
import { questionThreads, type QuestionThread } from '@/lib/change-order-events';
import { signedMoney } from '@/lib/change-order-ui';

// Room pages change the hash but not the token, so main.ts doesn't re-boot;
// the view listens itself. One listener at a time: a new share link replaces it.
let detachRoute: (() => void) | null = null;

export async function renderShareView(root: HTMLElement, token: string): Promise<void> {
  root.innerHTML = `<div class="share-shell"><div class="loading">Loading…</div></div>`;
  try {
    // Branding alongside the project, not after it: the header is drawn once,
    // with the right firm's name. A branding failure never blocks the portal.
    const [data, branding] = await Promise.all([
      fetchSharedProject(token),
      fetchShareBranding(token).catch(() => null),
    ]);
    if (!data) {
      // A dead link can't say whose it was: neutral, never a guessed firm.
      root.innerHTML = `
        <div class="share-shell">
          <div class="share-header">
            <div class="brand-name" style="font-family:var(--font-serif); font-size:18px">${esc(DEFAULT_COMPANY)}</div>
          </div>
          <main class="share-main">
            <div class="empty">
              <p>This share link is invalid or has expired.</p>
              <p class="muted">Please contact your designer for a new link.</p>
            </div>
          </main>
        </div>`;
      return;
    }
    brand = branding;
    setTitleBase(companyName(brand));
    paintShell(root);
    const body = root.querySelector<HTMLElement>('[data-share-body]')!;
    let lastRoom: string | null = null;
    const route = (): void => {
      const r = parseShareRoute(location.hash);
      // Another token (or leaving the portal) is main.ts's job: it re-boots.
      if (!r || r.token !== token) return;
      paintBody(body, data, token, r.roomId, lastRoom);
      lastRoom = r.roomId;
    };
    detachRoute?.();
    window.addEventListener('hashchange', route);
    detachRoute = () => window.removeEventListener('hashchange', route);
    route();

  } catch (err) {
    root.innerHTML = `<div class="share-shell"><div class="auth-error" style="margin:32px">${esc(err instanceof Error ? err.message : 'Failed to load')}</div></div>`;
  }
}

// Remembered across overview ⇄ room page, so "← All rooms" lands back on the
// tab the client came from rather than always on Overview.
let activeTab = 'overview';

// The sending firm's branding for this portal (0037) — set once per link.
let brand: Branding | null = null;

function paintShell(root: HTMLElement): void {
  activeTab = 'overview';
  // The firm's typeface, if it is not one the page already carries.
  ensureBrandFont(brand?.brand_font);
  root.innerHTML = `
    <div class="share-shell">
      <div class="share-header">
        ${brandLockup({
          branding: brand,
          variant: 'portal',
          logoUrl: logoPath(brand) ? logoUrl(logoPath(brand)!) : null,
          photoUrl: photoPath(brand) ? logoUrl(photoPath(brand)!) : null,
          tagSuffix: 'Client view',
        })}
      </div>
      <main class="share-main" data-share-body></main>
    </div>
  `;
}

function footer(): string {
  return `
  <footer class="share-footer">
    <div>Shared by ${esc(companyName(brand))}</div>
    <div class="muted small">Read-only view — contact your designer to make changes.</div>
  </footer>`;
}

function paintBody(
  body: HTMLElement, d: SharedProject, token: string,
  roomId: string | null, cameFrom: string | null,
): void {
  if (roomId) {
    const page = roomPage(d, roomId);
    body.innerHTML = (page ? renderRoomPage(d, token, page) : `
      <a class="share-back" href="${esc(shareHref(token))}">← All rooms</a>
      <div class="empty">
        <p>This room isn't part of the project anymore.</p>
        <p class="muted">Your designer may have renamed or removed it.</p>
      </div>`) + footer();
    setPageTitle(`${page ? page.room.name + ' · ' : ''}${d.project.name} · Client view`);
    window.scrollTo(0, 0);
  } else {
    body.innerHTML = renderOverview(d, token) + footer();
    setPageTitle(`${d.project.name} · Client view`);
    selectTab(body, activeTab);
    if (cameFrom) {
      // Back from a room: put the client where they left off, not at the top.
      const card = body.querySelector<HTMLElement>(`[data-room-card="${CSS.escape(cameFrom)}"]`);
      if (card && activeTab === 'overview') card.scrollIntoView({ block: 'center' });
      else window.scrollTo(0, 0);
    }
  }

  void hydratePortalImages(body, (paths) => signPortalMedia('share', token, paths));
  bindTabs(body);
  bindActions(body, token);
}

function renderOverview(d: SharedProject, token: string): string {
  const p = d.project;
  // The same revised budget the designer sees (0038): original + approved
  // change orders, the ones listed on the Change Orders tab.
  const coTotals = summarizeChangeOrders(d.change_orders ?? []);
  const hasCover = imageSource(p.hero_image_url) !== null;
  const { interior, exterior } = roomsByLocation(d.rooms);
  return `
        <nav class="tab-bar" data-share-tabs>
          <button class="tab is-active" data-tab-target="overview">Overview</button>
          <button class="tab" data-tab-target="selections">Selections</button>
          <button class="tab" data-tab-target="finishes">Finishes</button>
          <button class="tab" data-tab-target="changes">Change Orders${
            d.change_orders?.some((co) => co.status === 'sent') ? ' <span class="tab-dot"></span>' : ''
          }</button>
          <button class="tab" data-tab-target="style">Style brief</button>
        </nav>

        <div class="tab-panel" data-tab="overview">
        <header class="client-hero ${hasCover ? 'has-image' : ''}">
          ${hasCover ? `<div class="client-hero-image">${mediaImg(p.hero_image_url)}</div>` : ''}
          <div class="client-hero-body">
            <div class="eyebrow">${esc(p.client_name ?? '')}</div>
            <h1 class="client-hero-title">${esc(p.name)}</h1>
            ${p.address ? `<div class="client-hero-sub">${esc(p.address)}</div>` : ''}
            ${p.description ? `<p class="client-hero-desc">${esc(p.description)}</p>` : ''}
            <div class="hero-meta">
              <div><span class="muted">Timeline</span><div>${fmtDate(p.start_date)} → ${fmtDate(p.target_completion_date)}</div></div>
              <div><span class="muted">Budget</span><div>${fmtMoney(revisedBudgetCents(p.budget_cents, coTotals))}</div>${
                coTotals.approvedCount > 0 && p.budget_cents != null
                  ? `<div class="muted small">${esc(fmtMoney(p.budget_cents))} original ${esc(signedMoney(coTotals.approvedTotalCents))} in ${coTotals.approvedCount} approved change${coTotals.approvedCount === 1 ? '' : 's'}</div>`
                  : ''}</div>
              <div><span class="muted">Interior</span><div>${interior.length}</div></div>
              ${exterior.length ? `<div><span class="muted">Exterior</span><div>${exterior.length}</div></div>` : ''}
              <div><span class="muted">Materials</span><div>${d.specs.length}</div></div>
            </div>
          </div>
        </header>

        ${(() => {
          // Replaced the milestone progress bars in Phase 18. Same helper the
          // designer's "Coming up" panel uses, so the client sees exactly the
          // work the designer is looking at — no second definition of "next".
          const next = upcomingEntries(d.schedule, { limit: 6 });
          if (next.length === 0) return '';
          const roomName = new Map(d.rooms.map((r) => [r.id, r.name] as const));
          return `
          <section class="panel">
            <div class="panel-head"><h2>What's next</h2></div>
            ${renderUpcoming(next, roomName)}
          </section>`;
        })()}

        ${renderRoomGrid('Interior', interior, d, token)}
        ${renderRoomGrid('Exterior', exterior, d, token)}

        <!-- The "Updates" panel was here until 0048. Project notes are the
             designer's own working notes, so they are no longer in the
             payload; what a client should know is SENT to them instead. -->

        </div><!-- /overview tab -->

        <div class="tab-panel" data-tab="selections" hidden>
          ${d.rooms.map((r) => {
            const roomSpecs = d.specs.filter((s) => s.room_id === r.id);
            if (roomSpecs.length === 0) return '';
            return `
              <section class="panel">
                <div class="panel-head"><h2>${roomLink(token, r)}</h2><span class="muted">${r.is_exterior ? 'Exterior' : 'Interior'}</span></div>
                ${renderSpecList(roomSpecs)}
              </section>`;
          }).join('') || `<div class="empty-inline">No selections shared yet.</div>`}
        </div>

        <div class="tab-panel" data-tab="finishes" hidden>
          ${d.palette.filter((c) => !c.room_id).length === 0 ? '' : `
            <section class="panel">
              <div class="panel-head"><h2>Project palette</h2></div>
              ${renderPaletteGrid(d.palette.filter((c) => !c.room_id))}
            </section>`}
          ${d.rooms.map((r) => {
            const blocks = renderImageBlocks(groupRoomImages(d.room_images, r.id));
            const colors = d.palette.filter((c) => c.room_id === r.id);
            if (!blocks && colors.length === 0) return '';
            return `
              <section class="panel">
                <div class="panel-head"><h2>${roomLink(token, r)}</h2></div>
                ${colors.length ? `<div class="share-room-palette"><h4>Paint palette</h4>${renderPaletteGrid(colors)}</div>` : ''}
                ${blocks}
              </section>`;
          }).join('')}
        </div>

        <div class="tab-panel" data-tab="changes" hidden>
          ${renderChangeOrdersTab(d)}
        </div>

        <div class="tab-panel" data-tab="style" hidden>
        ${(() => {
          if (!d.questionnaire) return `<div class="empty-inline">No style brief shared yet.</div>`;
          const r = readResponses(d.questionnaire);
          const filled = QUESTIONNAIRE_FIELDS.filter((f) => (r[f.key] ?? '').trim() !== '');
          if (filled.length === 0) return `<div class="empty-inline">No style brief shared yet.</div>`;
          return `
            <section class="panel">
              <div class="panel-head"><h2>Style brief</h2></div>
              <div class="questionnaire-summary">
                ${filled.map((f) => `
                  <div class="q-summary">
                    <div class="q-label">${esc(f.label)}</div>
                    <div class="q-answer">${esc(r[f.key] ?? '')}</div>
                  </div>`).join('')}
              </div>
            </section>`;
        })()}
        </div>`;
}

type Room = SharedProject['rooms'][number];

function roomLink(token: string, r: Room): string {
  return `<a class="share-room-link" href="${esc(shareHref(token, r.id))}">${esc(r.name)}</a>`;
}

function renderRoomGrid(title: string, rooms: Room[], d: SharedProject, token: string): string {
  if (rooms.length === 0) return '';
  return `
    <section class="panel">
      <div class="panel-head"><h2>${esc(title)}</h2></div>
      <div class="rooms-grid">
        ${rooms.map((r) => {
          const open = d.room_feedback.filter((f) => f.room_id === r.id && !f.resolved_at);
          const cover = mediaImg(r.hero_image_url);
          // The title link stretches over the whole card (CSS ::after); the
          // buttons and requests sit above it, so they stay clickable on their own.
          return `
          <div class="room-card room-card-linked" data-room-card="${esc(r.id)}">
            ${cover ? `<div class="room-card-thumb">${cover}</div>` : ''}
            <div class="room-card-body">
              <h3><a class="room-card-link" href="${esc(shareHref(token, r.id))}">${esc(r.name)}</a></h3>
              <div class="room-card-meta">
                <span class="chip chip-${esc(r.status)}">${esc(ROOM_STATUS_LABEL[r.status])}</span>
                <span class="room-card-open">View room →</span>
              </div>
              ${renderStamps(r)}
              ${renderFeedback(open)}
              <div class="room-card-actions">${renderRoomActions(r)}</div>
            </div>
          </div>`;
        }).join('')}
      </div>
    </section>`;
}

function renderRoomPage(d: SharedProject, token: string, page: NonNullable<ReturnType<typeof roomPage>>): string {
  const r = page.room;
  const cover = mediaImg(r.hero_image_url);
  const next = upcomingEntries(page.schedule, { limit: 6 });
  const blocks = renderImageBlocks(page.images);
  return `
    <a class="share-back" href="${esc(shareHref(token))}">← All rooms</a>

    <header class="client-hero ${cover ? 'has-image' : ''}">
      ${cover ? `<div class="client-hero-image">${cover}</div>` : ''}
      <div class="client-hero-body">
        <div class="eyebrow">${esc(d.project.name)}</div>
        <h1 class="client-hero-title">${esc(r.name)}</h1>
        <div class="client-hero-sub">${r.is_exterior ? 'Exterior' : 'Interior'}</div>
        <div class="room-card-meta">
          <span class="chip chip-${esc(r.status)}">${esc(ROOM_STATUS_LABEL[r.status])}</span>
        </div>
        ${renderStamps(r)}
        <div class="room-card-actions">${renderRoomActions(r)}</div>
      </div>
    </header>

    ${page.openFeedback.length === 0 ? '' : `
      <section class="panel">
        <div class="panel-head"><h2>Your requests</h2></div>
        ${renderFeedback(page.openFeedback)}
      </section>`}

    ${next.length === 0 ? '' : `
      <section class="panel">
        <div class="panel-head"><h2>Coming up</h2></div>
        ${renderUpcoming(next, null)}
      </section>`}

    ${page.specs.length === 0 ? '' : `
      <section class="panel">
        <div class="panel-head"><h2>Materials</h2><span class="muted">${page.specs.length}</span></div>
        ${renderSpecList(page.specs)}
      </section>`}

    ${blocks ? `
      <section class="panel">
        <div class="panel-head"><h2>Photos</h2></div>
        ${blocks}
      </section>` : ''}

    ${page.palette.length === 0 ? '' : `
      <section class="panel">
        <div class="panel-head"><h2>Paint palette</h2></div>
        ${renderPaletteGrid(page.palette)}
      </section>`}

    ${page.isEmpty ? `<div class="empty-inline">Your designer hasn't added materials or photos to this room yet.</div>` : ''}

    <a class="share-back" href="${esc(shareHref(token))}">← All rooms</a>`;
}

function renderStamps(r: Room): string {
  const approved = approvalDetail(r);
  const completed = completionStamp(r);
  return `${approved ? `<div class="muted approved-stamp">${esc(approved)}</div>` : ''}${
    completed ? `<div class="muted approved-stamp">${esc(completed)}</div>` : ''}`;
}

function renderFeedback(open: SharedProject['room_feedback']): string {
  if (open.length === 0) return '';
  return `
    <ul class="room-feedback">
      ${open.map((f) => `
        <li>
          <div class="room-feedback-body">${esc(f.body)}</div>
          <div class="muted">${esc(f.author_name)} · ${esc(fmtDate(f.created_at))}</div>
        </li>`).join('')}
    </ul>`;
}

function renderRoomActions(r: Room): string {
  // See clientRoomControls: branching on 'approved' alone offered
  // "I approve" on a COMPLETED room.
  const c = clientRoomControls(r.status);
  return `${c.canApprove
    ? `<button class="btn-primary btn-sm" data-approve-room="${esc(r.id)}">I approve this room</button>`
    : ''}
    <button class="btn-link" data-room-feedback="${esc(r.id)}"${
      c.changeWithdrawsApproval ? ' data-was-approved="1"' : ''}>Request a change</button>`;
}

function renderUpcoming(
  next: SharedProject['schedule'], roomName: Map<string, string> | null,
): string {
  return `
    <ul class="upcoming">
      ${next.map((e) => `
        <li class="upcoming-item">
          <div class="upcoming-when ${e.start_date ? '' : 'muted'}">${
            e.start_date
              ? esc(fmtDate(e.start_date) + (e.end_date && e.end_date !== e.start_date ? ` → ${fmtDate(e.end_date)}` : ''))
              : 'To be scheduled'
          }</div>
          <div class="upcoming-body">
            <div class="upcoming-task">${esc(e.task)}</div>
            ${roomName ? `<div class="muted">${esc(roomName.get(e.room_id) ?? '')}</div>` : ''}
          </div>
          <span class="chip chip-${esc(e.status)}">${esc(SCHEDULE_STATUS_LABEL[e.status])}</span>
        </li>`).join('')}
    </ul>`;
}

function renderSpecList(specs: SharedProject['specs']): string {
  return `<ul class="share-spec-list">${specs.map((s) => `
    <li>
      <span>${s.thumbnail_url ? `<img class="spec-thumb" data-storage-path="${esc(s.thumbnail_url)}" alt="" /> ` : ''}${esc(s.item)}${s.source_url ? ` <a href="${esc(s.source_url)}" target="_blank" rel="noopener">↗</a>` : ''}</span>
      <span class="chip chip-${esc(s.status)}">${esc(SPEC_STATUS_LABEL[s.status])}</span>
    </li>`).join('')}</ul>`;
}

function renderPaletteGrid(colors: SharedProject['palette']): string {
  return `
    <div class="palette-grid">
      ${colors.map((c) => `
        <div class="color-swatch" style="cursor:default">
          <span class="swatch" style="background:${esc(c.hex)}"></span>
          <span class="swatch-meta">
            <span class="swatch-name">${esc(c.name)}</span>
            <span class="muted">${esc(c.hex)}${c.code ? ` · ${esc(c.code)}` : ''}</span>
          </span>
        </div>`).join('')}
    </div>`;
}

function selectTab(scope: HTMLElement, key: string): void {
  const tabBar = scope.querySelector<HTMLElement>('[data-share-tabs]');
  if (!tabBar) return;
  if (!tabBar.querySelector(`[data-tab-target="${CSS.escape(key)}"]`)) key = 'overview';
  activeTab = key;
  tabBar.querySelectorAll<HTMLButtonElement>('.tab').forEach((b) => {
    b.classList.toggle('is-active', b.dataset.tabTarget === key);
  });
  scope.querySelectorAll<HTMLElement>('.tab-panel[data-tab]').forEach((p) => {
    p.hidden = p.dataset.tab !== key;
  });
}

function bindTabs(scope: HTMLElement): void {
  scope.querySelectorAll<HTMLButtonElement>('[data-share-tabs] .tab').forEach((btn) => {
    btn.addEventListener('click', () => selectTab(scope, btn.dataset.tabTarget!));
  });
}

function bindActions(root: HTMLElement, token: string): void {
  // Room approval. querySelectorAll, not querySelector — one button per room.
  root.querySelectorAll<HTMLButtonElement>('[data-approve-room]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.approveRoom!;
      const typed = prompt('To approve this room, type your full name as your signature:');
      if (!typed || typed.trim().length < 2) return;
      try {
        const ok = await approveRoomViaShare(token, id, typed.trim());
        if (!ok) {
          alert("Couldn't approve — the share link may have expired, or this room was already approved.");
          return;
        }
        location.reload();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Approval failed');
      }
    });
  });

  // Request a change. Submitting on an approved room retracts the approval —
  // the server does that, so the reload below shows the corrected state.
  root.querySelectorAll<HTMLButtonElement>('[data-room-feedback]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.roomFeedback!;
      // Requesting a change on an approved room withdraws that approval (the
      // RPC does it server-side). Say so before they type, rather than letting
      // them discover it after the page reloads without their sign-off.
      if (btn.dataset.wasApproved === '1' && !confirm(
        'This room is approved. Requesting a change will withdraw your approval '
        + 'so your designer knows it needs another look.\n\nContinue?')) return;
      const body = prompt('What would you like changed?');
      if (!body || body.trim().length < 2) return;
      const typed = prompt('And your full name, so your designer knows who asked:');
      if (!typed || typed.trim().length < 2) return;
      try {
        const ok = await submitRoomFeedbackViaShare(token, id, typed.trim(), body.trim());
        if (!ok) {
          alert("Couldn't send — the share link may have expired.");
          return;
        }
        location.reload();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Could not send your request');
      }
    });
  });

  // Change-order acknowledge buttons.
  root.querySelectorAll<HTMLButtonElement>('[data-ack-co]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.ackCo!;
      const typed = prompt(
        'To approve this change order, type your full name as your signature. Once approved, it is added to your project budget.',
      );
      if (!typed || typed.trim().length < 2) return;
      try {
        const ok = await acknowledgeChangeOrderViaShare(token, id, typed.trim());
        if (!ok) {
          alert("Couldn't acknowledge — the share link may have expired or the change order is no longer available.");
          return;
        }
        location.reload();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Acknowledge failed');
      }
    });
  });

  // 0047 — a question instead of, or as well as, an approval.
  //
  // Deliberately not a rejection: unlike room feedback, asking does NOT retract
  // an approval. A client who has already approved and then wants to check
  // something has not changed their mind, and telling them it did would make
  // them hesitate to ask.
  root.querySelectorAll<HTMLButtonElement>('[data-ask-co]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.askCo!;
      const body = prompt('What would you like to ask about this change order?');
      if (!body || body.trim().length < 2) return;
      const typed = prompt('And your full name, so your designer knows who asked:');
      if (!typed || typed.trim().length < 2) return;
      try {
        const ok = await askChangeOrderQuestion(token, id, typed.trim(), body.trim());
        if (!ok) {
          alert("Couldn't send — the share link may have expired.");
          return;
        }
        location.reload();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Could not send your question');
      }
    });
  });
}

function renderChangeOrdersTab(d: SharedProject): string {
  const rows = d.change_orders ?? [];
  const visible = rows.filter((co) => co.status === 'sent' || co.status === 'acknowledged' || co.status === 'rejected');
  // Grouped through the same helper the designer's panel uses, so a question
  // reads the same on both sides. Empty on a portal served before 0047.
  const questions = d.change_order_questions ?? [];
  const threadsFor = (id: string): QuestionThread[] => questionThreads(
    questions.map((q) => ({
      id: q.id, change_order_id: q.change_order_id, kind: q.kind,
      at: q.at, actor_name: q.author_name, via: null, note: q.body,
    })), id);
  if (visible.length === 0) {
    return `<div class="empty-inline">No change orders have been shared yet.</div>`;
  }
  return `
    <section class="panel">
      <div class="panel-head"><h2>Change Orders</h2></div>
      <ul class="change-order-list">
        ${visible.map((co) => `
          <li class="change-order-card">
            <div class="change-order-head">
              <div>
                <div class="muted small">Change Order #${co.number}</div>
                <h3>${esc(co.title)}</h3>
              </div>
              <span class="chip chip-${esc(co.status)}">${esc(CHANGE_ORDER_STATUS_LABEL[co.status])}</span>
            </div>
            <p class="change-order-scope">${esc(co.scope)}</p>
            ${changeOrderTotal(co) !== 0 || co.cost_delta_cents !== 0 ? `
              <dl class="change-order-cost">
                <div><dt class="muted">Work</dt><dd>${esc(signedMoney(co.cost_delta_cents))}</dd></div>
                ${co.designer_fee_cents ? `<div><dt class="muted">Design fee</dt><dd>${esc(signedMoney(co.designer_fee_cents))}</dd></div>` : ''}
                <div class="change-order-total"><dt>Total</dt><dd><strong>${esc(signedMoney(changeOrderTotal(co)))}</strong></dd></div>
              </dl>` : ''}
            ${co.status === 'acknowledged' ? `
              <div class="change-order-ack muted small">
                Approved${co.acknowledged_by_name ? ' by ' + esc(co.acknowledged_by_name) : ''}
                ${co.acknowledged_at ? ' on ' + esc(fmtDate(co.acknowledged_at)) : ''}.
              </div>` : ''}
            ${renderChangeOrderThread(threadsFor(co.id))}
            <div class="change-order-actions">
              ${co.status === 'sent' ? `
                <button class="btn-primary" data-ack-co="${esc(co.id)}">I approve this change</button>` : ''}
              <button class="btn-link" data-ask-co="${esc(co.id)}">Ask a question</button>
            </div>
          </li>`).join('')}
      </ul>
    </section>
  `;
}

/**
 * A client's own question, and what their designer said back.
 *
 * Shown to the client so a question doesn't vanish into nothing — the one thing
 * that makes someone ask the same thing again by email.
 */
function renderChangeOrderThread(threads: QuestionThread[]): string {
  if (threads.length === 0) return '';
  return `
    <div class="change-order-questions">
      ${threads.map((t) => `
        <div class="co-question">
          <div class="co-question-body">${esc(t.question.note ?? '')}</div>
          <div class="muted small">You asked · ${esc(fmtDate(t.question.at))}</div>
          ${t.answers.length === 0
            ? `<div class="muted small">Your designer hasn’t answered yet.</div>`
            : t.answers.map((a) => `
                <div class="co-answer">
                  <div>${esc(a.note ?? '')}</div>
                  <div class="muted small">${esc(a.actor_name ?? 'Your designer')} · ${esc(fmtDate(a.at))}</div>
                </div>`).join('')}
        </div>`).join('')}
    </div>`;
}

function renderImageBlocks(g: ReturnType<typeof groupRoomImages>): string {
  if (g.total === 0) return '';
  const thumb = (i: SharedProject['room_images'][number]): string =>
    `<img class="room-image-thumb" data-storage-path="${esc(i.storage_path)}" alt="${esc(i.caption ?? '')}" />`;
  let html = '';
  if (g.pairs.length > 0) {
    html += `<div class="share-ba"><h4>Before & After</h4><div class="before-after-pairs">`;
    for (const [before, after] of g.pairs) {
      html += `
        <div class="before-after-pair">
          <figure><div class="ba-label">Before</div><img data-storage-path="${esc(before.storage_path)}" alt="${esc(before.caption ?? '')}" /></figure>
          <figure><div class="ba-label">After</div><img data-storage-path="${esc(after.storage_path)}" alt="${esc(after.caption ?? '')}" /></figure>
        </div>`;
    }
    html += `</div></div>`;
  }
  if (g.unpaired.length > 0) {
    const label = g.unpaired.every((i) => i.kind === 'before') ? 'Before'
      : g.unpaired.every((i) => i.kind === 'after') ? 'After' : 'Before & After';
    html += `<div class="share-gallery"><h4>${label}</h4><div class="room-image-grid">${g.unpaired.map(thumb).join('')}</div></div>`;
  }
  if (g.inspiration.length > 0) {
    html += `<div class="share-gallery"><h4>Inspiration</h4><div class="room-image-grid">${g.inspiration.map(thumb).join('')}</div></div>`;
  }
  if (g.inProgress.length > 0) {
    html += `<div class="share-gallery"><h4>In progress</h4><div class="room-image-grid">${g.inProgress.map(thumb).join('')}</div></div>`;
  }
  if (g.detail.length > 0) {
    html += `<div class="share-gallery"><h4>Details</h4><div class="room-image-grid">${g.detail.map(thumb).join('')}</div></div>`;
  }
  return html;
}
